import { Router, RouterModule, Routes } from '@angular/router';
import { LayoutComponent } from './shared/layout/layout.component';
import { authGuard } from './core/guards/auth/auth.guard';
import { NgModule } from '@angular/core';
import { kamRequiredGuard } from '@src/app/core/guards/kam/kam.guard';
import { poseedorFinalGuard } from '@src/app/core/guards/poseedor-final/poseedor-final.guard';
import { poseedorFinalIntegralGuard } from '@src/app/core/guards/poseedor-final-integral/poseedor-final-integral.guard';
import { poseedorFinalHomeGuard } from '@src/app/core/guards/poseedor-final-home/poseedor-final-home.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: 'home',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/home/home.component').then((m) => m.HomeComponent),
          },
          {
            path: 'packaging-declaration',
            loadComponent: () =>
              import(
                '@pages/home/pages/packaging-declaration/packaging-declaration.component'
              ).then((m) => m.PackagingDeclarationComponent),
          },
          {
            path: 'packaging-declaration/step-by-step',
            loadComponent: () =>
              import(
                '@pages/home/pages/packaging-declaration/step-by-step/step-by-step.component'
              ).then((m) => m.StepByStepComponent),
          },
          {
            path: 'packaging-declaration/platform-news',
            loadComponent: () =>
              import(
                '@pages/home/pages/packaging-declaration/platform-news/platform-news.component'
              ).then((m) => m.PlatformNewsComponent),
          },
          {
            path: 'producers-registration',
            loadComponent: () =>
              import(
                '@pages/home/pages/producers-registration/producers-registration.component'
              ).then((m) => m.ProducersRegistrationComponent),
          },
          {
            path: 'prevention-plans',
            loadComponent: () =>
              import(
                '@pages/home/pages/prevention-plans/prevention-plans.component'
              ).then((m) => m.PreventionPlansComponent),
          },
          {
            path: 'audit-report',
            loadComponent: () =>
              import(
                '@src/app/pages/home/pages/audit-report/audit-report.component'
              ).then((m) => m.AuditReportComponent),
          },
        ],
      },
      {
        path: 'waste-data-management',
        loadComponent: () =>
          import(
            '@pages/home/pages/waste-data-management/waste-data-management.component'
          ).then((m) => m.WasteDataManagementComponent),
        canActivate: [poseedorFinalHomeGuard],
      },
      {
        path: 'account-management',
        children: [
          {
            path: 'billing',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/account-management/billing/billing.component'
                  ).then((m) => m.BillingComponent),
              },
              {
                path: 'edit-bank-data',
                loadComponent: () =>
                  import(
                    '@pages/account-management/edit-bank-data/edit-bank-data.component'
                  ).then((m) => m.EditBankDataComponent),
              },
              {
                path: 'payment-flexibility',
                loadComponent: () =>
                  import(
                    '@pages/account-management/payment-flexibility/payment-flexibility.component'
                  ).then((m) => m.PaymentFlexibilityComponent),
              },
            ],
          },
          {
            path: 'contacts',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/account-management/contacts-management-page/contacts-management-page.component'
                  ).then((m) => m.ContactsManagementPageComponent),
              },
              {
                path: 'new',
                loadComponent: () =>
                  import(
                    '@pages/account-management/contacts-management-page/new-contact/new-contact.component'
                  ).then((m) => m.NewContactComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'services',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/services/services.component').then(
                (m) => m.ServicesComponent,
              ),
          },
          {
            path: 'packaging-management',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/packaging-management/packaging-management.component'
                  ).then((m) => m.PackagingManagementComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/packaging-management/pages/request-packaging-management/request-packaging-management.component'
                  ).then((m) => m.RequestPackagingManagementComponent),
              },
            ],
          },
          {
            path: 'declaration-support',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/declaration-support/declaration-support.component'
                  ).then((m) => m.DeclarationSupportComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/declaration-support/pages/request-declaration-support/request-declaration-support.component'
                  ).then((m) => m.RequestDeclarationSupportComponent),
              },
            ],
          },
          {
            path: 'network-conformity',
            canActivate: [kamRequiredGuard],
            loadComponent: () =>
              import(
                '@pages/services/pages/network-conformity/network-conformity.component'
              ).then((m) => m.NetworkConformityComponent),
          },
          {
            path: 'waste-data-support',
            canActivate: [poseedorFinalIntegralGuard],
            loadComponent: () =>
              import(
                '@pages/services/pages/waste-data-support/waste-data-support.component'
              ).then((m) => m.WasteDataSupportComponent),
          },
          {
            path: 'producer-registry',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/producer-registry/producer-registry.component'
                  ).then((m) => m.ProducerRegistryComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/producer-registry/pages/request-producer-registry/request-producer-registry.component'
                  ).then((m) => m.RequestProducerRegistryComponent),
              },
            ],
          },
          {
            path: 'circular-check',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/circular-check/circular-check.component'
                  ).then((m) => m.CircularCheckComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/circular-check/pages/request-circular-check/request-circular-check.component'
                  ).then((m) => m.RequestCircularCheckComponent),
              },
            ],
          },
          {
            path: 'circular-packaging-challenge',
            loadComponent: () =>
              import(
                '@pages/services/pages/circular-packaging-challenge/circular-packaging.component'
              ).then((m) => m.CircularPackagingComponent),
          },
          {
            path: 'innovation-consulting',
            loadComponent: () =>
              import(
                '@pages/services/pages/innovation-consulting/innovation-consulting.component'
              ).then((m) => m.InnovationConsultingComponent),
            canActivate: [kamRequiredGuard],
          },
          {
            path: 'waste-consulting',
            loadComponent: () =>
              import(
                '@pages/services/pages/waste-consulting/waste-consulting.component'
              ).then((m) => m.WasteConsultingComponent),
            canActivate: [poseedorFinalGuard],
          },
          {
            path: 'waste-consulting/personalized-guidance',
            loadComponent: () =>
              import(
                '@pages/services/pages/waste-consulting/personalized-guidance/personalized-guidance.component'
              ).then((m) => m.PersonalizedGuidanceComponent),
            canActivate: [poseedorFinalGuard],
          },
          {
            path: 'sustainable-work-centers',
            loadComponent: () =>
              import(
                '@pages/services/pages/sustainable-work-centers/sustainable-work-centers.component'
              ).then((m) => m.SustainableWorkCentersComponent),
          },
          {
            path: 'plant-tour',
            loadComponent: () =>
              import(
                '@pages/services/pages/plant-tour/plant-tour.component'
              ).then((m) => m.PlantTourComponent),
          },
          {
            path: 'libera-project',
            loadComponent: () =>
              import(
                '@pages/services/pages/libera-project/libera-project.component'
              ).then((m) => m.LiberaProjectComponent),
          },
          {
            path: 'eu-desk',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@src/app/pages/services/pages/eu-desk/eu-desk.component'
                  ).then((m) => m.EuDeskComponent),
              },
              {
                path: 'application',
                loadComponent: () =>
                  import(
                    '@src/app/pages/services/pages/eu-desk/application/application.component'
                  ).then((m) => m.ApplicationComponent),
              },
            ],
          },
          {
            path: 'prevention-plans',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@src/app/pages/services/pages/prevention-plans/prevention-plans.component'
                  ).then((m) => m.PreventionPlansComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/prevention-plans/pages/request-prevention-plans/request-prevention-plans.component'
                  ).then((m) => m.RequestPreventionPlansComponent),
              },
            ],
          },
          {
            path: 'regulatory-advice',
            loadComponent: () =>
              import(
                '@pages/services/pages/regulatory-advice/regulatory-advice.component'
              ).then((m) => m.RegulatoryAdviceComponent),
          },
          {
            path: 'aire',
            loadComponent: () =>
              import('@pages/services/pages/aire/aire.component').then(
                (m) => m.AireComponent,
              ),
            canActivate: [kamRequiredGuard],
          },
          {
            path: 'sirap-constitutions',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/sirap-constitutions/sirap-constitutions.component'
                  ).then((m) => m.SirapConstitutionsComponent),
              },
              {
                path: 'request',
                loadComponent: () =>
                  import(
                    '@pages/services/pages/sirap-constitutions/pages/request-sirap-constitutions/request-sirap-constitutions.component'
                  ).then((m) => m.RequestSirapConstitutionsComponent),
              },
            ],
          },
          {
            path: 'observatory',
            loadComponent: () =>
              import(
                '@pages/services/pages/observatory/observatory.component'
              ).then((m) => m.ObservatoryComponent),
          },
          {
            path: 'programs',
            loadComponent: () =>
              import('@pages/services/pages/programs/programs.component').then(
                (m) => m.ProgramsComponent,
              ),
          },
          {
            path: 'sector-guides',
            loadComponent: () =>
              import(
                '@pages/services/pages/sector-guides/sector-guides.component'
              ).then((m) => m.SectorGuidesComponent),
            canActivate: [kamRequiredGuard],
          },
          {
            path: 'kit-communication',
            loadComponent: () =>
              import(
                '@pages/services/pages/kit-communication/kit-communication.component'
              ).then((m) => m.KitCommunicationComponent),
          },
          {
            path: 'elearning',
            loadComponent: () =>
              import(
                '@pages/services/pages/elearning/elearning.component'
              ).then((m) => m.ElearningComponent),
          },
          {
            path: 'ad-hoc',
            loadComponent: () =>
              import('@pages/services/pages/ad-hoc/ad-hoc.component').then(
                (m) => m.AdHocComponent,
              ),
            canActivate: [kamRequiredGuard],
          },
          {
            path: 'waste-data-support',
            loadComponent: () =>
              import(
                '@pages/services/pages/waste-data-support/waste-data-support.component'
              ).then((m) => m.WasteDataSupportComponent),
            canActivate: [poseedorFinalIntegralGuard],
          },
        ],
      },
      {
        path: 'requests',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/requests/requests.component').then(
                (m) => m.RequestsComponent,
              ),
          },
          {
            path: 'new-request',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@pages/requests/pages/new-request/new-request.component'
                  ).then((m) => m.NewRequestComponent),
              },
              {
                path: 'operational',
                loadComponent: () =>
                  import(
                    '@pages/requests/pages/new-request/pages/new-request-operational/new-request-operational.component'
                  ).then((m) => m.NewRequestOperationalComponent),
              },
              {
                path: 'certificates',
                loadComponent: () =>
                  import(
                    '@pages/requests/pages/new-request/pages/new-request-certificates/new-request-certificates.component'
                  ).then((m) => m.NewRequestCertificatesComponent),
              },
              {
                path: 'complaints',
                loadComponent: () =>
                  import(
                    '@pages/requests/pages/new-request/pages/new-request-complaints/new-request-complaints.component'
                  ).then((m) => m.NewRequestComplaintsComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'news',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/news/news.component').then((m) => m.NewsComponent),
          },
          {
            path: 'detail',
            loadComponent: () =>
              import(
                '@pages/news/pages/news-detail/news-detail.component'
              ).then((m) => m.NewsDetailComponent),
          },
        ],
      },
      {
        path: 'training',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@pages/training/training.component').then(
                (m) => m.TrainingComponent,
              ),
          },
          {
            path: 'courses',
            loadComponent: () =>
              import(
                '@pages/training/training-courses/training-courses.component'
              ).then((m) => m.TrainingCoursesComponent),
          },
          {
            path: 'webinars',
            loadComponent: () =>
              import(
                '@pages/training/training-webinars/training-webinars.component'
              ).then((m) => m.TrainingWebinarsComponent),
          },
          {
            path: 'selflearning',
            loadComponent: () =>
              import(
                '@pages/training/training-selflearning/training-selflearning.component'
              ).then((m) => m.TrainingSelflearningComponent),
          },
        ],
      },
      {
        path: 'help',
        loadComponent: () =>
          import('@pages/user-help/user-help.component').then(
            (m) => m.UserHelpComponent,
          ),
      },
      {
        path: 'feedback',
        loadComponent: () =>
          import('@pages/feedback/feedback.component').then(
            (m) => m.FeedbackComponent,
          ),
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('@pages/calendar/calendar.component').then(
            (m) => m.CalendarComponent,
          ),
      },
    ],
    canActivate: [authGuard],
  },
  {
    path: 'login',
    loadComponent: () =>
      import('@pages/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'callback',
    loadComponent: () =>
      import('@pages/callback/callback.component').then(
        (m) => m.CallbackComponent,
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('@pages/forbidden/forbidden.component').then(
        (m) => m.ForbiddenComponent,
      ),
  },
  {
    path: '**',
    redirectTo: 'home',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
      scrollOffset: [0, 64],
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutesModule {}
